import React, { ReactNode } from "react"
import { SubmitHandler } from "react-hook-form"
import { Modal as MantineModal, Group } from "@mantine/core"

import { Button, Stack } from "@/components"
import { useStyles } from "@/components/CoreModal/Modal.styles"

interface ModalProps<FormValues = undefined> {
    /** Title of the Modal **/
    title?: string
    children: ReactNode
    opened: boolean
    /** Handler to close the modal */
    close: () => void
    /** Text to show in the button to confirm the Modal **/
    confirmLabel?: string
    /** Text to show in the button to cancel the Modal **/
    cancelLabel?: string
    /** how to proceed if user presses confirm Button **/
    onConfirm?: SubmitHandler<FormValues>
    /** how to proceed if user presses cancel Button **/
    onCancel?: SubmitHandler<FormValues>
    /** Modal Size in Width (pixels) - Defaults to 320 **/
    size?: number | string
    /** Handler to close the modal on click outside */
    closeOnClickOutside?: boolean
    /** Classname for the header **/
    headerClassName?: string
    /** Classname for the body **/
    bodyClassName?: string
    /** Classname for the root **/
    rootClassName?: string
    /** Classname for the footer **/
    footerClassName?: string
    /** Button Variants **/
    buttonVariants?: string[]
    /** Submit Button Disabled **/
    submitDisabled?: boolean
    /** Impact Reports Data Plum Attributes for the create template button **/
    impactReportsDataPlumAttributes?: Record<string, string>
}

export const CoreModal = ({
    title,
    children,
    opened,
    close,
    confirmLabel = "Continue",
    cancelLabel = "Cancel",
    onConfirm,
    onCancel,
    size = 320,
    closeOnClickOutside = true,
    headerClassName,
    bodyClassName,
    footerClassName,
    rootClassName,
    buttonVariants,
    submitDisabled,
    impactReportsDataPlumAttributes,
}: ModalProps) => {
    const { classes, cx } = useStyles({ maxWidth: size })

    return (
        <MantineModal.Root
            opened={opened}
            onClose={close}
            centered
            size={size}
            closeOnClickOutside={closeOnClickOutside}
            className={rootClassName || undefined}
        >
            <MantineModal.Overlay />
            <MantineModal.Content className={classes.modalContent}>
                <MantineModal.Header className={cx(classes.modalHeader, headerClassName && headerClassName)}>
                    <MantineModal.Title className={classes.modalTitle}>{title}</MantineModal.Title>
                    <MantineModal.CloseButton aria-label="Cancel" />
                </MantineModal.Header>
                <MantineModal.Body className={cx(classes.modalBody, bodyClassName && bodyClassName)}>
                    <Stack align={"center"}>{children}</Stack>
                </MantineModal.Body>
                <Group className={cx(classes.modalButtons, footerClassName && footerClassName)}>
                    <Button
                        onClick={onCancel || close}
                        variant={buttonVariants?.length ? buttonVariants[0] : "filled"}
                        className={classes.actionButton}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        onClick={onConfirm || close}
                        variant={buttonVariants?.length ? buttonVariants[1] : "outline"}
                        className={classes.actionButton}
                        disabled={submitDisabled}
                        {...impactReportsDataPlumAttributes}
                    >
                        {confirmLabel}
                    </Button>
                </Group>
            </MantineModal.Content>
        </MantineModal.Root>
    )
}
